import { MatrixHeaders, DocumentInfo } from '@/components/admin/documents/editor/types.ts'
import {
  Classification,
  Classifications,
  DropinMetadata,
  DropinMetadataList,
  MatrixPeriod,
} from '@/stores/DocumentEditor/state'
import { docTypes } from '@/components/admin/documents/upload/utils.js'

export const isDropin = (box) => box.classType === 'dropins' || box.type === 'dropins'

export const classificationDisplayAddress = (
  box: Classification,
  classifications: Classifications | undefined,
  metadata: DropinMetadataList,
): string => {
  const address = box.tableBoxAddress ?? `${box.pageNumber}:${box.tableBoxNumber}`
  if (!classifications?.some((entry) => entry.pageNumber === box.pageNumber && entry.isDeletedDropin)) {
    return address
  }
  if (!isDropin(box) && !box.overriddenByDropin) {
    return address
  }
  const sheetName = box.sheetName ?? `${box.pageNumber}_${box.tableBoxNumber + 1}`
  const remaining = metadata
    .filter((entry) => entry.pageNumber === box.pageNumber)
    .toSorted((a, b) => Number(a.sheetName.split('_')[1]) - Number(b.sheetName.split('_')[1]))
  const index = remaining.findIndex((entry) => entry.sheetName === sheetName)
  return index < 0 ? address : `${box.pageNumber}:${index}`
}

export const findClassification = (classifications: Classifications, item: Classification) => {
  return classifications.find(
    (c) =>
      c.pageNumber === item.pageNumber &&
      c.tableBoxNumber === item.tableBoxNumber &&
      !c.overriddenByDropin &&
      typeof c.classType === typeof item.classType,
  )
}

export const findClassificationIndex = (classifications: Classifications, item: Classification) => {
  return classifications.findIndex(
    (c) =>
      c.pageNumber === item.pageNumber &&
      c.tableBoxNumber === item.tableBoxNumber &&
      !c.overriddenByDropin &&
      typeof c.classType === typeof item.classType,
  )
}

export const findClassificationToDelete = (
  classifications: Classifications,
  item: Classification,
  tableBoxNumbers: number[],
) => {
  return classifications.find(
    (classification) =>
      classification.pageNumber === item.pageNumber &&
      !tableBoxNumbers.includes(classification.tableBoxNumber) &&
      typeof classification.classType === typeof item.classType &&
      !classification.overriddenByDropin,
  )
}

export const findClassificationIndexToDelete = (
  classifications: Classifications,
  item: Classification,
  tableBoxNumbers: number[],
) => {
  return classifications.findIndex(
    (classification) =>
      classification.pageNumber === item.pageNumber &&
      !tableBoxNumbers.includes(classification.tableBoxNumber) &&
      typeof classification.classType === typeof item.classType &&
      !classification.overriddenByDropin,
  )
}

export const isDropinClassification = (box: Classification, dropinMetadata: DropinMetadataList) =>
  dropinMetadata.some(
    (metadata) =>
      metadata.pageNumber === box.pageNumber &&
      (metadata.sheetName
        ? metadata.sheetName === (box.sheetName ?? `${box.pageNumber}_${box.tableBoxNumber + 1}`)
        : metadata.tableNumber === box.tableBoxNumber),
  )

export const addFieldsToDropinMetadata = (dropinMetadata: DropinMetadataList) => {
  return dropinMetadata.map((metadata) => {
    metadata.tableBoxAddress = `${metadata.pageNumber}:${metadata.tableNumber}`
    metadata.classType = 'dropins'
    metadata.tableBoxNumber = metadata.tableNumber
    metadata.id = crypto.randomUUID()
    metadata.isDeletableManualClassification = true
    return metadata
  })
}

export const syncClassificationAndDropin = (box: Classification, dropinMetadata: DropinMetadataList) => {
  box.overriddenByDropin = true

  const dropin = dropinMetadata.find((dropin) => isDropinClassification(box, [dropin]))
  if (dropin) {
    box.sheetName = dropin.sheetName
    box.main = dropin.main
    box.tableType = dropin.tableType
    box.tableContext = dropin.tableContext
  }

  return box
}

export const findClassificationAndSyncWithDropin = (classifications: Classifications, dropin: DropinMetadata) => {
  const originalIndex = classifications.findIndex(
    (c) =>
      !isDropin(c) &&
      (dropin.sheetName
        ? isDropinClassification(c, [dropin])
        : c.pageNumber === dropin.pageNumber && c.tableBoxNumber === dropin.tableBoxNumber),
  )
  const original = classifications[originalIndex]

  if (original) {
    original.main = dropin.main
    original.tableType = dropin.tableType
    original.tableContext = dropin.tableContext
  }

  return { originalIndex, original }
}

export const handleDeleteDropin = (updatedClassifications, toBeDeletedItem) => {
  // If there are any dropins on a page, it is a dropin page
  const isDropinPage = updatedClassifications.some((c) => c.pageNumber === toBeDeletedItem.pageNumber && isDropin(c))
  updatedClassifications = updatedClassifications.map((c) => {
    if (c.pageNumber !== toBeDeletedItem.pageNumber || isDropin(c)) {
      return c
    }

    if (c.overriddenByDropin) {
      c.overriddenByDropin = isDropinPage
    }
    return c
  })
  return updatedClassifications
}

export const sortAndIndexClassifications = (classifications: Classifications) => {
  return classifications
    .sort((a, b) => {
      return a.pageNumber === b.pageNumber ? a.tableBoxNumber - b.tableBoxNumber : a.pageNumber - b.pageNumber
    })
    .map((classification, index) => {
      classification.index = index
      return classification
    })
}

export const createNewClassification = (overrides) => {
  // Need to update createdBy
  const newClassification = {
    documentId: null,
    classScore: null,
    companyId: null,
    processIdentifier: null,
    createdBy: null,
    createdAt: new Date(),
    comment: null,
    enabled: true,
    active: true,
    hasText: null,
    boxScore: null,
    boxType: null,
    height: null,
    width: null,
    y: null,
    x: null,
    tableBoxAddress: null,
    pageNumber: 'need',
    tableBoxNumber: null,
    tableType: null,
    main: false,
    classType: 'manual',
    tableContext: null,
    uuid: null,
    isNew: true,
    index: null,
    id: crypto.randomUUID(),
  }

  return { ...newClassification, ...overrides }
}

export const sortHTML = (html) => {
  const sortedHTML = html

  const firstTableType = html.find((item) => item.tables.length > 0)?.tables[0].sheetType

  const allSameTableType = sortedHTML.every((item) => item.tables.every((table) => table.sheetType === firstTableType))
  if (allSameTableType) {
    // If all tables are of the same type, assume the input is already period-ascending
    // and flip it so the most recent period appears first.
    return sortedHTML.reverse()
  }

  const matrixTableTypeOrder = ['IS', 'CF', 'BS', 'DN', 'EB', 'GS', 'KP', 'OT', 'IE', 'IG', 'DE', 'GK', 'EG', 'EK']
  sortedHTML.forEach((html, index) => {
    sortedHTML[index].tables = html.tables.sort(
      (a, b) => matrixTableTypeOrder.indexOf(a.sheetType) - matrixTableTypeOrder.indexOf(b.sheetType),
    )
  })
  return sortedHTML
}

export const matrixCellClass = 'matrix-table-cell'
export const matrixHeaderClass = 'matrix-table-header'
export const largeCellClass = 'large-cell'
export const mediumCellClass = 'medium-cell'
export const emptyRequiredCellClass = 'empty-required-cell'
export const currentDocCellClass = 'current-doc-cell'
export const htmlViewCellClass = 'html-view-cell'

export const updateShowContext = (classification: Classification, showContext: boolean) => {
  let tableContext = classification.tableContext

  if (!tableContext || !tableContext.content) {
    tableContext = showContext ? { tableAddress: classification.tableBoxAddress, action: 'Prepend', content: '' } : null
  }

  const updatedClassification = {
    ...classification,
    tableContext: tableContext,
    showContext: showContext,
  }

  return updatedClassification
}

export const getValuesForTableTypeCell = (headers: MatrixHeaders, period: MatrixPeriod, valueKey: string): string[] => {
  const values: string[] = []
  const valueHeaders = headers.map((header) => header.text).filter((val) => !!val)
  Object.entries(period).filter(([key, value]) => {
    if (valueHeaders.includes(key)) {
      value[valueKey].forEach((docId: string) => {
        values.push(docId)
      })
    }
  })
  return values
}

export const getDocNameFromYearAndPeriod = (documentType: string, documentInfo?: DocumentInfo): string | undefined => {
  const ticker = documentInfo?.documentName.split('_')[0]
  if (ticker && documentInfo) {
    const { period, year, month } = documentInfo
    if (documentType === docTypes.preliminaries) {
      return `${ticker}_${period}${year.slice(-2)}_prelim.pdf`
    } else if (documentType === docTypes.presentation) {
      return `${ticker}_${period}${year.slice(-2)}_P.pdf`
    } else if (documentType === docTypes.sustainability) {
      return `${ticker}_${period}${year.slice(-2)}_S.pdf`
    } else if (documentType === docTypes.financials) {
      return `${ticker}_${period}${year.slice(-2)}.pdf`
    } else if (documentType === docTypes.prospectus) {
      return `${ticker}_${year}${month}.pdf`
    }
  }
}

export const getYearAndPeriodFromDocName = (
  documentType: string,
  documentInfo?: DocumentInfo,
): { year: string; month?: string; period?: string } | undefined => {
  const periodYear = documentInfo?.documentName.split('_')[1]
  if (periodYear && documentInfo) {
    if (documentType === docTypes.prospectus) {
      return { year: periodYear.slice(0, 4), month: periodYear.slice(4, 6) }
    } else {
      return { period: periodYear.slice(0, 2), year: `20${periodYear.slice(2, 4)}` }
    }
  }
}

export const normalizeClassifications = (classifications: Classifications): Classifications => {
  return classifications.map((c) => {
    if (!c.id && !c.isDeletableManualClassification) {
      c.id = crypto.randomUUID()
      c.isDeletableManualClassification = c.classType === 'manual' || isDropin(c)
    }
    // Normalize tableContext: if action is null but content exists, set action to 'Prepend'
    if (c.tableContext && c.tableContext.action === null && c.tableContext.content) {
      c.tableContext = { ...c.tableContext, action: 'Prepend' }
    }
    return c
  })
}
